import { Modal } from "ui/Modal"
import { useTranslation } from "react-i18next"
import { Button, Card, Flex, Form } from "antd"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import styles from "./index.module.css"
import TimeOffTypeSwitcher from "./TimeOffTypeSwitcher"
import ExistingTimeOffDetails from "./ExistingTimeOffDetails"
import PropTypes from "prop-types"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"
import { useDispatch, useSelector } from "react-redux"
import { createCalendarTimeOff, getCalendarBookings, updateCalendarTimeOff } from "store/calendar"
import { TIME_OFF_BE_KEYS } from "../../constants"
import { toaster } from "components/common/Toast"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { useEffect, useState } from "react"
import { formatDate, formatTime } from "../../utils"
import { AlertIcon } from "ui/Icon/DSIcon/AlertIcon"

const ScheduleTimeOff = ({ open, onCancel, setIsScheduleTimeOffModalOpen, dates }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const { isMobile } = useDevice()
  const { timeOffSave } = useSelector((state) => state.calendar)
  const [editingTimeOffItem, setEditingTimeOffItem] = useState(null)
  const [isDirty, setIsDirty] = useState(false)

  const buildWeekDaysPayload = (values, editingItem) => {
    const days = Object.keys(values)
      .filter((key) => key.startsWith("startTime_"))
      .map((key) => key.replace("startTime_", ""))

    const unavailableDays = days.map((day) => ({
      unavailable_weekday: day?.toLowerCase(),
      all_day: values?.[`fullDay_${day}`],
      start_time: formatTime(values?.[`startTime_${day}`]),
      end_time: formatTime(values?.[`endTime_${day}`])
    }))
    const isEditing = !!editingItem
    const first = unavailableDays[0] || {}
    return {
      unavailable_type: TIME_OFF_BE_KEYS.weekDays,
      ...(isEditing
        ? {
            unavailable_weekday: first?.unavailable_weekday,
            start_time: first?.start_time,
            end_time: first?.end_time,
            all_day: first?.all_day
          }
        : { days: unavailableDays }),
      note: values?.notes,
      recurring_yearly: values?.recurringYearly,
      start_date: formatDate(values?.weeksStartDate),
      ...(values?.weeksEndDate && { end_date: formatDate(values?.weeksEndDate) })
    }
  }

  const buildDateRangePayload = (values) => ({
    unavailable_type: TIME_OFF_BE_KEYS.dateRange,
    start_date: formatDate(values?.dateRangeFromDate),
    end_date: formatDate(values?.dateRangeToDate),
    recurring_yearly: values?.recurringYearly,
    note: values?.notes,
    start_time: formatTime(values?.dateRangeFromTime),
    end_time: formatTime(values?.dateRangeToTime)
  })

  const buildSingleDayPayload = (values) => ({
    unavailable_type: TIME_OFF_BE_KEYS.singleDay,
    unavailable_date: formatDate(values?.singleDayDate),
    start_time: formatTime(values?.singleDayStartTime),
    end_time: formatTime(values?.singleDayEndTime),
    all_day: values?.singleDayFullDay,
    recurring_yearly: values?.recurringYearly,
    note: values?.notes
  })

  const handleApiResponse = (promise) => {
    promise.then((response) => {
      if (response?.message) {
        toaster.success(response.message)
      }
      form.resetFields()
      setIsDirty(false)
      setIsScheduleTimeOffModalOpen(false)
      dispatch(
        getCalendarBookings({
          "q[date_gteq]": dates.start,
          "q[date_lteq]": dates.end
        })
      )
    })
  }

  const onFinish = (values) => {
    const { timeOffType } = values || {}
    const builders = {
      [TIME_OFF_BE_KEYS.weekDays]: buildWeekDaysPayload,
      [TIME_OFF_BE_KEYS.dateRange]: buildDateRangePayload,
      [TIME_OFF_BE_KEYS.singleDay]: buildSingleDayPayload
    }
    const makePayload = builders[timeOffType]
    if (!makePayload) return
    const payload = makePayload(values, editingTimeOffItem)
    if (editingTimeOffItem) {
      handleApiResponse(dispatch(updateCalendarTimeOff(editingTimeOffItem?.id, payload)))
    } else {
      handleApiResponse(dispatch(createCalendarTimeOff(payload)))
    }
  }

  const handleCancel = () => {
    // Show exit confirmation only when there are unsaved changes
    if (isDirty) {
      onCancel() // triggers ExitScheduleTimeOff modal in parent
    } else {
      setIsScheduleTimeOffModalOpen(false) // close directly when nothing changed
    }
  }

  useEffect(() => {
    if (open) setIsDirty(false)
  }, [open])

  useEffect(() => {
    setIsDirty(false)
  }, [editingTimeOffItem])

  const modalContent = (
    <FirstLoading loading={timeOffSave?.loading}>
      <Flex vertical gap={spacings.L} className={styles.modalContent}>
        <Flex align="center" gap={spacings.XS} className={styles.warningContainer}>
          <AlertIcon />
          <Text>{t("calendar.schedule_time_off.timeoff_warning")}</Text>
        </Flex>
        <Form
          onFinish={onFinish}
          onValuesChange={() => setIsDirty(true)}
          form={form}
          initialValues={{
            recurringYearly: false,
            notes: ""
          }}
        >
          <Card>
            <Flex vertical gap={spacings.XL}>
              <TimeOffTypeSwitcher
                form={form}
                editingTimeOffItem={editingTimeOffItem}
                setEditingTimeOffItem={setEditingTimeOffItem}
                dates={dates}
              />
            </Flex>
          </Card>
          {isMobile ? (
            <div className="mt-l">
              <ExistingTimeOffDetails form={form} setEditingTimeOffItem={setEditingTimeOffItem} />
            </div>
          ) : (
            <Card className="mt-l">
              <ExistingTimeOffDetails form={form} setEditingTimeOffItem={setEditingTimeOffItem} />
            </Card>
          )}
          <Flex gap={spacings.S} className={`mt-4xl ${styles.modalActions}`}>
            <Button variant="outlined" color="primary" onClick={handleCancel}>
              {t("global.cancel")}
            </Button>
            <Button htmlType="submit" type="primary">
              {editingTimeOffItem ? t("calendar.schedule_time_off.save_changes") : t("global.save")}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </FirstLoading>
  )
  return isMobile ? (
    <CustomDrawer
      open={open}
      setOpen={setIsScheduleTimeOffModalOpen}
      onCancel={handleCancel}
      title={t("calendar.schedule_time_off.schedule_time_off")}
      width="100%"
    >
      {modalContent}
    </CustomDrawer>
  ) : (
    <Modal centered open={open} onCancel={handleCancel} title={t("calendar.schedule_time_off.schedule_time_off")} footer={null} width={800}>
      {modalContent}
    </Modal>
  )
}

ScheduleTimeOff.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  setIsScheduleTimeOffModalOpen: PropTypes.func.isRequired,
  dates: PropTypes.object.isRequired
}

export default ScheduleTimeOff
